import React, { useMemo, useState } from "react";
import LayoutContainer from "@components/layout/LayoutContainer";
import styled, { DefaultTheme } from "styled-components/native";
import { View } from "react-native";
import { useTheme } from "styled-components/native";
import VDBText from "@components/atoms/Text";
import { useTranslation } from "react-i18next";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import CompanyDetailsScreen from "./CompanyDetailsScreen";
import UsersSeatsScreen from "./UsersSeatsScreen";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Spacer } from "@components/atoms";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { menu } from "../MoreScreen/utils";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { EventName, EventType, ViewType, ViewTypeEnum } from "@utils/analytics";

type MenuItem = { id: string; label: string; route: string };
const menuConfig = (menu as MenuItem[]).map((m) => ({
  ...m,
  route: (Routes as any)[
    Object.keys(Routes).find((k) => (Routes as any)[k] === m.route) as any
  ] as unknown as string,
})) as unknown as { id: string; label: string; route: Routes }[];

const AccountOverviewScreen = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { typography } = useScreenOptionsHooks();
  const [activeId, setActiveId] = useState<string>(menuConfig[0].id);
  const { trackButtonEvent } = useUserAnalytics() || {};
  const isSplit = theme.isLargeScreen;

  const onSelect = (item: MenuItem) => {
    trackButtonEvent?.(
      EventName.BUTTON_CLICK,
      item.id === "company_details"
        ? EventType.COMPANY_DETAILS_TAB_CLICK
        : EventType.USER_SEATS_TAB_CLICK,
      { component_type: ViewType.SCREEN_VIEW },
      item.id === "company_details"
        ? ViewTypeEnum?.COMPANY_DETAILS_SCREEN_VIEW
        : ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
    );
    setActiveId(item.id);
    if (!isSplit) {
      navigate(item.route as never);
    }
  };

  const leftList = useMemo(
    () => (
      <LeftPanel isSplit={isSplit}>
        <VDBText type={typography["Lead-M"]} color={theme["core-05"]}>
          {t("accountOverView.title.text", "Account Profile")}
        </VDBText>
        <Spacer size={SpaceType.Big} />
        {menuConfig.map((item, index) => {
          const isActive = activeId === item.id && isSplit;
          return (
            <>
              <LeftItem
                key={item.id}
                onPress={() => onSelect(item)}
                style={{
                  backgroundColor: theme["core-01"],
                  borderColor: isActive ? theme["main-01"] : theme["core-01"],
                }}
              >
                <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
                  {t(item.label) || item.label}
                </VDBText>
                <VDBIcon
                  name={VDBIcons.ARROW_RIGHT}
                  size={24}
                  color={theme["core-04"]}
                  variant={IconsVariant.VDBCustom}
                />
              </LeftItem>
              {index !== menuConfig.length - 1 && (
                <Spacer size={SpaceType.Basic} />
              )}
            </>
          );
        })}
      </LeftPanel>
    ),
    [activeId, theme, typography]
  );

  return (
    <LayoutContainer>
      {isSplit ? (
        <SplitContainer>
          {leftList}
          <RightPanel>
            {activeId === "company_details" && <CompanyDetailsScreen />}
            {activeId === "users_seats" && <UsersSeatsScreen />}
          </RightPanel>
        </SplitContainer>
      ) : (
        <View style={{ flex: 1 }}>{leftList}</View>
      )}
    </LayoutContainer>
  );
};

const SplitContainer = styled.View`
  flex: 1;
  flex-direction: row;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
`;

const LeftPanel = styled.ScrollView<{ isSplit: boolean }>`
  width: ${({ isSplit }) => (isSplit ? "30%" : "100%")};
  flex: ${({ isSplit }) => (isSplit ? 2 : 1)};
  border-right-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  padding: ${SpaceType.Big}px;
  background-color: ${({
    theme,
    isSplit,
  }: {
    theme: DefaultTheme;
    isSplit: boolean;
  }) => (isSplit ? theme["core-01"] : theme["core-02"])};
`;

const LeftItem = styled.TouchableOpacity`
  padding: ${SpaceType.Double}px;
  border-radius: ${SpaceType.Basic}px;
  border-width: 2px;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
`;

const RightPanel = styled.View`
  flex: 7;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
`;

export default AccountOverviewScreen;
